import { Checkbox } from '@chakra-ui/react';
import { ReactNode, useCallback, useMemo } from 'react';
import {
  Column,
  Row,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { StopClickPropagation } from '~components/ui/StopClickPropagation';
import { SubscriptionTag } from '~components/ui/SubscriptionTag';
import { generateTableCellComponent } from '~components/ui/TableCell';
import { getSubscriptionType, isWaveSubscription } from '~utils/subscriptions';
import { SubscriptionCustomersTableCustomerFragment as CustomerFragment } from './__generated__/SubscriptionCustomersTable.graphql';
import { Columns, SelectableSubscriptionCustomersTableCustomerFragment } from './types';

const TableCell = generateTableCellComponent<
  SelectableSubscriptionCustomersTableCustomerFragment,
  ReactNode
>();

const TableCellBold = generateTableCellComponent<
  SelectableSubscriptionCustomersTableCustomerFragment,
  ReactNode
>({
  isBold: true,
});

export function useSubscriptionCustomersTable(
  data: CustomerFragment[],
  currentCustomerIds: string[],
) {
  const columns = useMemo<Array<Column<SelectableSubscriptionCustomersTableCustomerFragment>>>(
    () => [
      {
        id: Columns.Selection,
        Header: ({ getToggleAllPageRowsSelectedProps, page }) => {
          const { role, title } = getToggleAllPageRowsSelectedProps();

          const changeableRows: Array<Row<SelectableSubscriptionCustomersTableCustomerFragment>> =
            [];
          let allRowsSelected = true;
          let allRowsDeselected = true;

          for (const row of page) {
            if (
              !currentCustomerIds.includes(row.original.id) &&
              isWaveSubscription(row.original.waveSubscription)
            ) {
              // Skip rows that correlate to a customer that has a different subscription assigned, because that
              // row will be disabled
              continue;
            }

            changeableRows.push(row);

            if (row.isSelected) {
              allRowsDeselected = false;
            } else {
              allRowsSelected = false;
            }
          }

          const onChange = () => {
            changeableRows.forEach((row) => row.toggleRowSelected(!allRowsSelected));
          };

          return (
            <Checkbox
              isChecked={!allRowsDeselected}
              isIndeterminate={!allRowsSelected && !allRowsDeselected}
              onChange={onChange}
              role={role}
              title={title}
            />
          );
        },
        Cell: ({ row }: { row: Row<SelectableSubscriptionCustomersTableCustomerFragment> }) => {
          const { checked, indeterminate, onChange, role, title } = row.getToggleRowSelectedProps();

          // Cannot check a customer that has a different subscription than the one we're editing
          const isDisabled =
            !currentCustomerIds.includes(row.original.id) &&
            isWaveSubscription(row.original.waveSubscription);

          return (
            <StopClickPropagation>
              <Checkbox
                isDisabled={isDisabled}
                isChecked={checked}
                isIndeterminate={indeterminate}
                onChange={onChange}
                role={role}
                title={title}
              />
            </StopClickPropagation>
          );
        },
        width: '48px',
        sortType: (rowA, rowB) => {
          return (
            Number(rowA.original.isLinkedToSubscription) -
            Number(rowB.original.isLinkedToSubscription)
          );
        },
      },
      {
        id: Columns.Name,
        Header: 'Customer',
        accessor: 'name',
        Cell: TableCellBold,
        sortType: (rowA, rowB) =>
          rowA.original.name.toLowerCase().localeCompare(rowB.original.name.toLowerCase()),
        width: 'auto',
      },
      {
        id: Columns.Subscription,
        Header: 'Subscription',
        Cell: ({ row }: { row: Row<SelectableSubscriptionCustomersTableCustomerFragment> }) => {
          const subscriptionType = getSubscriptionType(row.original.waveSubscription);

          return <SubscriptionTag variant={subscriptionType} />;
        },
        width: '100px',
      },
      {
        id: Columns.Displays,
        Header: 'Displays',
        accessor: 'displayCount',
        Cell: TableCell,
        width: '100px',
      },
    ],
    [currentCustomerIds],
  );

  // The react-table docs specify that this *must* be memoized
  // Because the `useSortBy` plugin for `react-table` is executed _before_ `useRowSelect`, we don't have access to `isSelected` when sorting is applied.
  // We do want to sort on selection state, however, so down below, where we memoize the data, we will be adding that property manually
  const dataMemoized = useMemo<SelectableSubscriptionCustomersTableCustomerFragment[]>(
    () =>
      data.map((customer) => ({
        ...customer,
        isLinkedToSubscription: currentCustomerIds.includes(customer.id),
      })),
    [data, currentCustomerIds],
  );

  // The react-table docs specify that this *must* be memoized
  const getRowId = useCallback((row: CustomerFragment) => row.id, []);

  const selectedRowIds = useMemo(
    () =>
      currentCustomerIds.reduce<Record<string, boolean>>((map, current) => {
        map[current] = true;
        return map;
      }, {}),
    [currentCustomerIds],
  );

  return useTable(
    {
      columns,
      data: dataMemoized,
      initialState: {
        pageSize: 15,
        sortBy: [{ id: Columns.Selection, desc: true }, { id: Columns.Name }],
        selectedRowIds,
      },
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetPage: false,
      getRowId,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  );
}
